import React, { ReactNode, useState } from 'react';
import { Form } from 'react-bootstrap';
import { InfoHint } from '../../../../../components/hint/InfoHint';
import { SelectSingle } from '../../../components/Selects/SelectSingle';
import {
  GoalAchievementRow_GoalFragment,
  GoalAchievementRow_PredefinedResultOptionFragment,
} from '../../../__generated__/graphql';
import type { GoalResultChange } from '../../../components/EmployeeFeedbackQuestionnaire/InlineGoalQuestion';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { formatPredefinedResultOptionLabel } from '../Goals2/logic';
import { formatDecimal } from '../../../../../utils/number';

type GoalData = GoalAchievementRow_GoalFragment;

type PredefinedOption = GoalAchievementRow_PredefinedResultOptionFragment;

type Props = {
  goal: GoalData;
  categoryName: string;
  categoryWeight: number | null;
  predefinedResultOptions: readonly PredefinedOption[];
  canEdit: boolean;
  onChange: (change: GoalResultChange) => void;
  onGoalClick: (objectiveId: string) => void;
};

type ThresholdEntry = {
  from?: number;
  to?: number;
  score: number | 'linear';
  scoreFrom?: number;
  scoreTo?: number;
};

function findMatchingRange(
  thresholdsJson: string | null,
  resultValue: number,
): string | null {
  if (!thresholdsJson) return null;
  try {
    const entries: ThresholdEntry[] = JSON.parse(thresholdsJson);
    for (const entry of entries) {
      const fromOk = entry.from == null || resultValue >= entry.from;
      const toOk = entry.to == null || resultValue < entry.to;
      if (fromOk && toOk) {
        const range =
          entry.from == null && entry.to != null
            ? `<${entry.to}`
            : entry.from != null && entry.to == null
            ? `${entry.from}+`
            : `${entry.from}–${entry.to}`;
        const score =
          entry.score === 'linear'
            ? `${entry.scoreFrom ?? '?'}→${entry.scoreTo ?? '?'}`
            : String(entry.score);
        return `${range}: ${score}`;
      }
    }
  } catch {
    // ignore
  }
  return null;
}

function formatScaleDescription(
  scaleType: string | null,
  thresholdsJson: string | null,
  matchedRange: string | null = null,
): ReactNode | null {
  if (!scaleType) return null;
  switch (scaleType) {
    case 'LINEAR':
      return 'Linear: result = score';
    case 'CUSTOM_FLOW':
      return 'Custom scoring flow';
    case 'THRESHOLDS':
      return formatAllRanges(thresholdsJson, matchedRange);
    default:
      return null;
  }
}

function formatAllRanges(
  json: string | null,
  matchedRange: string | null,
): ReactNode | null {
  if (!json) return null;
  try {
    const entries: ThresholdEntry[] = JSON.parse(json);
    if (!Array.isArray(entries) || entries.length === 0) return null;
    return entries.map((entry, i) => {
      const line = formatRangeEntry(entry);
      const isMatched = line === matchedRange;
      return (
        <React.Fragment key={i}>
          {i > 0 && '\n'}
          {isMatched ? <strong>{line}</strong> : line}
        </React.Fragment>
      );
    });
  } catch {
    return null;
  }
}

function formatRangeEntry(entry: ThresholdEntry): string {
  const range =
    entry.from == null && entry.to != null
      ? `<${entry.to}`
      : entry.from != null && entry.to == null
      ? `${entry.from}+`
      : `${entry.from}–${entry.to}`;
  const score =
    entry.score === 'linear'
      ? `${entry.scoreFrom ?? '?'}→${entry.scoreTo ?? '?'}`
      : String(entry.score);
  return `${range}: ${score}`;
}

const i18Path = 'performanceReview.goalResultsSection';

export const GoalAchievementRow: React.FC<React.PropsWithChildren<Props>> = ({
  goal,
  categoryName,
  categoryWeight,
  predefinedResultOptions,
  canEdit,
  onChange,
  onGoalClick,
}) => {
  const t = useNamespacedTranslation(i18Path);
  const scaleName = goal.achievementScale?.Name ?? null;
  const scaleType = goal.achievementScale?.flair__Type__c ?? null;
  const thresholdsJson =
    goal.achievementScale?.flair__Thresholds_Json__c ?? null;
  const hasPredefinedOptions = predefinedResultOptions.length > 0;

  const initialPredefinedId =
    predefinedResultOptions.find((opt) => opt.Name === goal.displayResult)
      ?.Id ?? null;

  const [localValue, setLocalValue] = useState<string | null>(
    initialPredefinedId,
  );
  const [numericValue, setNumericValue] = useState<string>(
    !hasPredefinedOptions ? goal.displayResult ?? '' : '',
  );

  const handlePredefinedChange = (optionId: string | null) => {
    if (!canEdit) {
      return;
    }
    setLocalValue(optionId);
    onChange({
      objectiveId: goal.objectiveId,
      predefinedResultOptionId: optionId,
      result: null,
    });
  };

  const handleNumericChange = (value: string) => {
    if (!canEdit) {
      return;
    }
    setNumericValue(value);
    const parsed = value !== '' ? parseFloat(value) : null;
    onChange({
      objectiveId: goal.objectiveId,
      predefinedResultOptionId: null,
      result: parsed,
    });
  };

  const selectOptions = predefinedResultOptions.map((opt) => ({
    value: opt.Id,
    label: formatPredefinedResultOptionLabel(
      opt.Name,
      opt.flair__Result_Value__c,
    ),
  }));

  const resultNum =
    goal.displayResult != null ? parseFloat(goal.displayResult) : NaN;

  const scaleContent = (() => {
    const matchedRange =
      scaleType === 'THRESHOLDS' && !isNaN(resultNum)
        ? findMatchingRange(thresholdsJson, resultNum)
        : null;
    const desc = formatScaleDescription(
      scaleType,
      thresholdsJson,
      matchedRange,
    );
    return desc ? <InfoHint className="ms-1" text={desc} /> : null;
  })();

  const scoreContent =
    goal.achievementScore != null &&
    goal.displayResult != null &&
    scaleType === 'THRESHOLDS'
      ? (() => {
          const matchedRange = !isNaN(resultNum)
            ? findMatchingRange(thresholdsJson, resultNum)
            : null;
          return matchedRange ? (
            <InfoHint
              className="ms-1"
              text={t('scoreCalculationTooltip', {
                result: goal.displayResult,
                score: formatDecimal(goal.achievementScore),
                scaleName: matchedRange,
              })}
            />
          ) : null;
        })()
      : null;

  return (
    <tr>
      <td className="py-2">
        <button
          type="button"
          className="btn btn-link p-0 text-start"
          onClick={() => onGoalClick(goal.objectiveId)}>
          {goal.goalName}
        </button>
      </td>
      <td className="py-2">
        {categoryName}
        {categoryWeight != null && (
          <InfoHint
            className="ms-1"
            text={t('categoryWeightTooltip', {
              weight: formatDecimal(categoryWeight),
            })}
          />
        )}
      </td>
      <td className="py-2">
        {goal.finalWeight != null ? (
          <>
            {formatDecimal(goal.finalWeight)}%
            {categoryWeight != null && goal.goalWeight != null && (
              <InfoHint
                className="ms-1"
                text={t('finalWeightCalculationTooltip', {
                  categoryWeight: formatDecimal(categoryWeight),
                  goalWeight: formatDecimal(goal.goalWeight),
                  finalWeight: formatDecimal(goal.finalWeight),
                })}
              />
            )}
          </>
        ) : (
          '—'
        )}
      </td>
      <td className="py-2">
        {scaleName ?? '—'}
        {scaleContent}
      </td>
      <td className="py-2">
        {hasPredefinedOptions ? (
          <SelectSingle
            value={localValue}
            options={selectOptions}
            onChange={handlePredefinedChange}
            isDisabled={!canEdit}
            heightMode="small"
            placeholder={t('selectResultPlaceholder')}
          />
        ) : (
          <Form.Control
            type="number"
            size="sm"
            value={numericValue}
            disabled={!canEdit}
            onChange={(e) => handleNumericChange(e.target.value)}
            placeholder={t('enterResultPlaceholder')}
          />
        )}
      </td>
      <td className="py-2">
        {formatDecimal(goal.achievementScore)}
        {scoreContent}
      </td>
      <td className="py-2">
        {formatDecimal(goal.weightedAchievementScore)}
        {goal.weightedAchievementScore != null &&
          goal.achievementScore != null &&
          goal.finalWeight != null && (
            <InfoHint
              className="ms-1"
              text={t('weightedScoreCalculationTooltip', {
                score: formatDecimal(goal.achievementScore),
                finalWeight: formatDecimal(goal.finalWeight),
                weightedScore: formatDecimal(goal.weightedAchievementScore),
              })}
            />
          )}
      </td>
    </tr>
  );
};
